/**
 * WebMCP Integration Module (W3C WebMCP / Chrome EPP Specification)
 * Exposes Anter site capabilities and key actions to AI agents in the browser.
 */

export interface WebMcpTool {
  name: string;
  description: string;
  inputSchema: Record<string, unknown>;
  execute: (args: Record<string, unknown>) => Promise<Record<string, unknown>>;
}

export interface WebMcpContext {
  tools: WebMcpTool[];
}

declare global {
  interface Navigator {
    modelContext?: {
      tools: WebMcpTool[];
      provideContext?: (context: WebMcpContext) => void;
      registerTool?: (tool: WebMcpTool) => void;
    };
  }
}

export function registerWebMcpTools(): void {
  if (typeof window === "undefined" || typeof navigator === "undefined") return;

  const tools: WebMcpTool[] = [
    {
      name: "get_anter_overview",
      description:
        "Get comprehensive summary of Anter AI Agent Builder platform features, capabilities, architecture, and documentation links.",
      inputSchema: {
        type: "object",
        properties: {
          topic: {
            type: "string",
            description: "Optional topic filter: overview, flows, pricing, mcp, sdk, runtime",
            enum: ["overview", "flows", "pricing", "mcp", "sdk", "runtime"],
          },
        },
      },
      execute: async (args) => {
        const topic = (args.topic as string) || "overview";
        return {
          platform: "Anter — AI Agent Builder",
          topic,
          summary:
            "Anter is an app-first AI agent platform. Build multi-agent systems visually (Flows), execute with a ReAct runtime (transfer & delegate handoffs, tool calling, MCP), and ship production-ready APIs.",
          documentationUrl: "https://anter.ai/docs",
          apiCatalogUrl: "https://anter.ai/.well-known/api-catalog",
          mcpServerCardUrl: "https://anter.ai/.well-known/mcp/server-card.json",
          agentSkillsUrl: "https://anter.ai/.well-known/agent-skills/index.json",
        };
      },
    },
    {
      name: "request_architecture_review",
      description:
        "Submit an inbound lead for a custom Anter Architecture Review & Agent System Consultation.",
      inputSchema: {
        type: "object",
        properties: {
          email: { type: "string", description: "Corporate work email (e.g. name@company.com)" },
          company: { type: "string", description: "Company name" },
          dailyRuns: {
            type: "string",
            description: "Estimated daily agent runs, e.g. <1k, 1k-10k, >10k",
          },
          useCase: {
            type: "string",
            description: "Brief overview of agent architecture or use case",
          },
        },
        required: ["email", "company"],
      },
      execute: async (args) => {
        try {
          const res = await fetch("/api/inbound/architecture-review", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(args),
          });
          const data = await res.json();
          return data;
        } catch (err) {
          return { error: true, message: String(err) };
        }
      },
    },
    {
      name: "contact_engineering",
      description: "Send a technical engineering inquiry directly to the Anter engineering team.",
      inputSchema: {
        type: "object",
        properties: {
          email: { type: "string", description: "Corporate work email" },
          company: { type: "string", description: "Company name" },
          inquiryType: { type: "string", description: "Inquiry topic or classification" },
          message: { type: "string", description: "Inquiry message content" },
        },
        required: ["email", "company", "message"],
      },
      execute: async (args) => {
        try {
          const res = await fetch("/api/inbound/contact-engineering", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(args),
          });
          const data = await res.json();
          return data;
        } catch (err) {
          return { error: true, message: String(err) };
        }
      },
    },
  ];

  // Initialize navigator.modelContext polyfill container if absent
  if (!navigator.modelContext) {
    navigator.modelContext = {
      tools: [],
      provideContext(context: WebMcpContext) {
        if (context && Array.isArray(context.tools)) {
          this.tools.push(...context.tools);
        }
      },
      registerTool(tool: WebMcpTool) {
        if (tool) {
          this.tools.push(tool);
        }
      },
    };
  }

  // Provide tools via navigator.modelContext
  if (typeof navigator.modelContext.provideContext === "function") {
    navigator.modelContext.provideContext({ tools });
  } else {
    navigator.modelContext.tools.push(...tools);
  }
}

// Auto-register on import in browser context
if (typeof window !== "undefined") {
  registerWebMcpTools();
}
